import { ptBR } from 'date-fns/locale';
import {
  format as dateFnsFormat,
  parseISO as dateFnsParseISO,
  isValid as dateFnsIsValid,
  addDays as dateFnsAddDays,
} from 'date-fns';

export const DATE_FORMATS = {
  date: 'dd/MM/yyyy',
  dateWithWeekday: "EEEE, dd 'de' MMMM 'de' yyyy",
  dateShortMonth: "dd 'de' MMM 'de' yyyy",
  time: 'HH:mm',
  timeWithSeconds: 'HH:mm:ss',
  dateTime: 'dd/MM/yyyy HH:mm',
  dateTimeFull: "dd/MM/yyyy 'às' HH:mm",
  dateInput: 'yyyy-MM-dd',
  dateTimeInput: "yyyy-MM-dd'T'HH:mm",
  monthYear: "MMMM 'de' yyyy",
  monthShort: 'MMM',
} as const;

function ensureDate(input: Date | string | number | null | undefined): Date | null {
  if (input === null || input === undefined || input === '') return null;
  if (input instanceof Date) return dateFnsIsValid(input) ? input : null;
  try {
    const parsed = typeof input === 'number' ? new Date(input) : dateFnsParseISO(String(input));
    return dateFnsIsValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function formatDatePtBR(value: Date | string | number | null | undefined, fallback = '-'): string {
  const date = ensureDate(value);
  if (!date) return fallback;
  return dateFnsFormat(date, DATE_FORMATS.date, { locale: ptBR });
}

export function formatDateTimePtBR(value: Date | string | number | null | undefined, fallback = '-'): string {
  const date = ensureDate(value);
  if (!date) return fallback;
  return dateFnsFormat(date, DATE_FORMATS.dateTime, { locale: ptBR });
}

export function formatDateTimeFullPtBR(value: Date | string | number | null | undefined, fallback = '-'): string {
  const date = ensureDate(value);
  if (!date) return fallback;
  return dateFnsFormat(date, DATE_FORMATS.dateTimeFull, { locale: ptBR });
}

export function formatTimePtBR(value: Date | string | number | null | undefined, fallback = '-'): string {
  const date = ensureDate(value);
  if (!date) return fallback;
  return dateFnsFormat(date, DATE_FORMATS.time, { locale: ptBR });
}

export function formatMonthYearPtBR(value: Date | string | number | null | undefined, fallback = '-'): string {
  const date = ensureDate(value);
  if (!date) return fallback;
  return dateFnsFormat(date, DATE_FORMATS.monthYear, { locale: ptBR });
}

export function formatCurrencyBRL(value: number | null | undefined, fallback = '-'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback;
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function formatNumberPtBR(value: number | null | undefined, decimals = 0, fallback = '-'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback;
  return value.toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export function formatKmPtBR(value: number | null | undefined, fallback = '-'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback;
  return `${formatNumberPtBR(value)} km`;
}

export function formatLitersPtBR(value: number | null | undefined, fallback = '-'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return fallback;
  return `${formatNumberPtBR(value, 1)}L`;
}

export function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function toDateInputValue(value: Date | string | number | null | undefined): string {
  const date = ensureDate(value);
  if (!date) return '';
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  return `${y}-${m}-${d}`;
}

export function toDateTimeInputValue(value: Date | string | number | null | undefined): string {
  const date = ensureDate(value);
  if (!date) return '';
  const y = date.getFullYear();
  const m = pad2(date.getMonth() + 1);
  const d = pad2(date.getDate());
  const hh = pad2(date.getHours());
  const mm = pad2(date.getMinutes());
  return `${y}-${m}-${d}T${hh}:${mm}`;
}

export function parseDateFromInput(yyyy_mm_dd: string): Date | null {
  if (!yyyy_mm_dd || yyyy_mm_dd.length < 10) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(yyyy_mm_dd);
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0, 0);
  return dateFnsIsValid(date) ? date : null;
}

export function parseDateTimeFromInput(s: string): Date | null {
  if (!s || s.length < 16) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(s);
  if (!match) return null;
  const [, y, m, d, hh, mm] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), 0, 0);
  return dateFnsIsValid(date) ? date : null;
}

export function todayInputValue(): string {
  return toDateInputValue(new Date());
}

export function nowDateTimeInputValue(): string {
  return toDateTimeInputValue(new Date());
}

export function toISODateMiddayUTC(yyyy_mm_dd: string): string {
  const d = parseDateFromInput(yyyy_mm_dd);
  if (!d) return '';
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  return `${y}-${m}-${day}T12:00:00.000Z`;
}

export function toISODateTimeUTCFromInput(s: string): string {
  const d = parseDateTimeFromInput(s);
  if (!d) return '';
  return d.toISOString();
}

export function addDaysToDateInput(yyyy_mm_dd: string, days: number): string {
  const d = parseDateFromInput(yyyy_mm_dd);
  if (!d) return '';
  return toDateInputValue(dateFnsAddDays(d, days));
}

export function addDaysToDate(value: Date | string | number | null | undefined, days: number): Date | null {
  const d = ensureDate(value);
  if (!d) return null;
  return dateFnsAddDays(d, days);
}

export const ptBRLocale = ptBR;
